import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Typography,
} from '@mui/material';
import {
  Assessment as ResultsIcon,
  Search as SearchIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Schedule as PendingIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import ModernTable from '../../../components/ModernTable';
import StatsCard from '../../../components/StatsCard';
import api from '../../../api';
import { showError } from '../../../utils/sweetAlertConfig';

const PRIMARY = '#29204C';
const SUCCESS = '#68B977';

export default function Results() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [stats, setStats] = useState({
    total: 0,
    passed: 0,
    failed: 0,
    pending: 0,
  });

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = async () => {
    try {
      setLoading(true);
      // Replace with your actual API endpoint
      // const response = await api.get('/results');
      // setResults(response.data.results || []);
      // setStats(response.data.stats || stats);

      // Mock data for demonstration
      setTimeout(() => {
        setResults([]);
        setStats({
          total: 0,
          passed: 0,
          failed: 0,
          pending: 0,
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error loading results:', error);
      showError('Error', 'Failed to load results data');
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      passed: { label: 'Passed', color: SUCCESS, icon: <PassIcon fontSize="small" /> },
      failed: { label: 'Failed', color: '#d32f2f', icon: <FailIcon fontSize="small" /> },
      pending: { label: 'Pending', color: '#ed6c02', icon: <PendingIcon fontSize="small" /> },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        sx={{
          bgcolor: `${config.color}15`,
          color: config.color,
          fontWeight: 600,
          '& .MuiChip-icon': {
            color: config.color,
          },
        }}
      />
    );
  };

  const columns = [
    {
      field: 'candidate_name',
      label: 'Candidate',
      minWidth: 200,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: PRIMARY, width: 36, height: 36 }}>
            <PersonIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: PRIMARY }}>
              {row.candidate_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.candidate_id}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'job_title',
      label: 'Job Position',
      minWidth: 180,
    },
    {
      field: 'exam_score',
      label: 'Exam Score',
      width: 120,
      align: 'center',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: row.exam_score >= 70 ? SUCCESS : '#d32f2f',
          }}
        >
          {row.exam_score !== null ? `${row.exam_score}%` : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'interview_score',
      label: 'Interview Score',
      width: 140,
      align: 'center',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: row.interview_score >= 7 ? SUCCESS : '#d32f2f',
          }}
        >
          {row.interview_score !== null ? `${row.interview_score}/10` : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      label: 'Status',
      width: 130,
      align: 'center',
      render: (row) => getStatusChip(row.status),
    },
    {
      field: 'evaluated_date',
      label: 'Evaluated Date',
      width: 140,
      render: (row) =>
        row.evaluated_date
          ? new Date(row.evaluated_date).toLocaleDateString()
          : 'N/A',
    },
  ];

  const filteredResults = results.filter(
    (result) =>
      result.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.candidate_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.job_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedResults = filteredResults.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Results"
        subtitle="View and analyze candidate evaluation results"
        icon={ResultsIcon}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Results' },
        ]}
      />

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Results"
            value={stats.total}
            icon={ResultsIcon}
            color={PRIMARY}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Passed"
            value={stats.passed}
            icon={PassIcon}
            color={SUCCESS}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Failed"
            value={stats.failed}
            icon={FailIcon}
            color="#d32f2f"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Pending"
            value={stats.pending}
            icon={PendingIcon}
            color="#ed6c02"
          />
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by candidate name, ID, or job position..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: PRIMARY,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: PRIMARY,
              },
            },
          }}
        />
      </Box>

      {/* Results Table */}
      <ModernTable
        columns={columns}
        data={paginatedResults}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={filteredResults.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage="No results found. Candidate evaluations will appear here."
        showActions={false}
      />
    </Container>
  );
}
